import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useUI } from '../../context/UIContext';
import Pagination from '../../components/common/Pagination';
import PermissionGuard from '../../components/common/PermissionGuard';
import SortableHeader from '../../components/common/SortableHeader';
import * as XLSX from 'xlsx';

const SupplierManagement = () => {
    const { user } = useAuth();
    const { showToast, showConfirm } = useUI();
    const navigate = useNavigate();
    const [suppliers, setSuppliers] = useState([]);
    const [stats, setStats] = useState(null);
    const [filter, setFilter] = useState('Tous');
    const [search, setSearch] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [loading, setLoading] = useState(true);
    const [editingSupplier, setEditingSupplier] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalSuppliers, setTotalSuppliers] = useState(0);
    const [limit, setLimit] = useState(parseInt(localStorage.getItem('app_items_per_page')) || 25);
    const [sortBy, setSortBy] = useState('name');
    const [sortOrder, setSortOrder] = useState('ASC');
    const [formData, setFormData] = useState({
        name: '', contactEmail: '', contactPhone: '', category: '',
        address: '', website: '', status: 'Actif', deliveryScore: 0,
        avgDeliveryTime: '', paymentTerms: 'Net 30', paymentMethod: 'Virement bancaire',
        currency: 'EUR', earlyDiscount: '', certifications: '', notes: '', francoPort: ''
    });
    const [addressSuggestions, setAddressSuggestions] = useState([]);
    const [showAddressSuggestions, setShowAddressSuggestions] = useState(false);
    const addressDebounceRef = useRef(null);
    const [categories, setCategories] = useState([]);

    // Refs for performance stability
    const categoriesRef = useRef([]);

    const fetchData = useCallback(async () => {
        if (!user?.companyId) return;
        setLoading(true);
        try {
            const queryParams = new URLSearchParams({
                companyId: user.companyId,
                page: currentPage,
                limit: limit,
                sortBy: sortBy,
                sortOrder: sortOrder
            });
            if (filter !== 'Tous') queryParams.append('status', filter);

            const [suppRes, statsRes, catRes] = await Promise.all([
                fetch(`/api/suppliers?${queryParams.toString()}`),
                fetch(`/api/suppliers/stats?companyId=${user.companyId}`),
                fetch(`/api/categories?companyId=${user.companyId}`)
            ]);

            if (suppRes.ok) {
                const data = await suppRes.json();
                setSuppliers(data.suppliers || []);
                setTotalPages(data.totalPages || 1);
                setTotalSuppliers(data.total || 0);
            }
            if (statsRes.ok) setStats(await statsRes.json());
            if (catRes.ok) {
                const catData = await catRes.json();
                const names = catData.map(c => c.name);
                setCategories(names);
                categoriesRef.current = names;
            }
        } catch (err) {
            console.error('Error fetching suppliers:', err);
            showToast("Erreur lors du chargement des fournisseurs", "error");
        } finally {
            setLoading(false);
        }
    }, [user?.companyId, filter, currentPage, limit, sortBy, sortOrder, showToast]);

    useEffect(() => { fetchData(); }, [fetchData]);

    useEffect(() => {
        setCurrentPage(1);
    }, [filter]);

    const handleAddressSearch = (value) => {
        setFormData(prev => ({ ...prev, address: value }));
        if (addressDebounceRef.current) clearTimeout(addressDebounceRef.current);
        if (value.length < 3) { setAddressSuggestions([]); return; }
        addressDebounceRef.current = setTimeout(async () => {
            try {
                const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(value)}&limit=5&addressdetails=1`);
                const data = await res.json();
                setAddressSuggestions(data);
                setShowAddressSuggestions(true);
            } catch (e) {
                console.error(e);
            }
        }, 400);
    };

    const selectAddress = (suggestion) => {
        setFormData(prev => ({
            ...prev,
            address: suggestion.display_name
        }));
        setShowAddressSuggestions(false);
        setAddressSuggestions([]);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const url = editingSupplier ? `/api/suppliers/${editingSupplier.id}` : '/api/suppliers';
            const method = editingSupplier ? 'PUT' : 'POST';
            await fetch(url, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...formData, companyId: user.companyId })
            });
            showToast(editingSupplier ? 'Fournisseur modifié' : 'Fournisseur créé', 'success');
            setShowModal(false);
            setEditingSupplier(null);
            resetForm();
            fetchData();
        } catch (err) {
            showToast('Erreur lors de la sauvegarde', 'error');
        }
    };

    const handleDelete = useCallback((id) => {
        showConfirm({
            title: 'Supprimer ce fournisseur ?',
            message: 'Cette action est irréversible.',
            type: 'danger',
            onConfirm: async () => {
                await fetch(`/api/suppliers/${id}`, { method: 'DELETE' });
                showToast('Fournisseur supprimé', 'success');
                fetchData();
            }
        });
    }, [showConfirm, showToast, fetchData]);

    const resetForm = () => {
        setFormData({
            name: '', contactEmail: '', contactPhone: '', category: '',
            address: '', website: '', status: 'Actif', deliveryScore: 0,
            avgDeliveryTime: '', paymentTerms: 'Net 30', paymentMethod: 'Virement bancaire',
            currency: 'EUR', earlyDiscount: '', certifications: '', notes: '', francoPort: ''
        });
    };

    const openEdit = (s) => {
        setEditingSupplier(s);
        setFormData({
            name: s.name, contactEmail: s.contactEmail || '', contactPhone: s.contactPhone || '',
            category: s.category || '', address: s.address || '', website: s.website || '',
            status: s.status, deliveryScore: s.deliveryScore || 0,
            avgDeliveryTime: s.avgDeliveryTime || '', paymentTerms: s.paymentTerms || 'Net 30',
            paymentMethod: s.paymentMethod || 'Virement bancaire', currency: s.currency || 'EUR',
            earlyDiscount: s.earlyDiscount || '', certifications: s.certifications || '', notes: s.notes || '', francoPort: s.francoPort || ''
        });
        setShowModal(true);
    };

    const openNew = () => {
        setEditingSupplier(null);
        resetForm();
        setShowModal(true);
    };

    const getInitials = (name) => {
        if (!name) return "??";
        return name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
    };

    const handleExport = () => {
        try {
            showToast("Génération de l'export fournisseurs...", "info");
            const exportData = suppliers.map(s => ({
                'Nom': s.name,
                'Email Contact': s.contactEmail || '',
                'Téléphone': s.contactPhone || '',
                'Catégories': s.category || '',
                'Statut': s.status,
                'Délai Livraison Moyen': s.avgDeliveryTime ? `${s.avgDeliveryTime} jours` : 'N/A',
                'Score Livraison': s.deliveryScore || 0,
                'Date Création': new Date(s.createdAt).toLocaleDateString('fr-FR')
            }));

            const ws = XLSX.utils.json_to_sheet(exportData);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'Fournisseurs');

            const date = new Date().toISOString().slice(0, 10);
            XLSX.writeFile(wb, `Export_Fournisseurs_${date}.xlsx`);
            showToast("Export réussi", "success");
        } catch (error) {
            console.error('Export error:', error);
            showToast("Erreur lors de l'exportation", "error");
        }
    };

    const filtered = (suppliers || [])
        .filter(s => {
            const name = s?.name || "";
            const email = s?.contactEmail || "";
            return name.toLowerCase().includes(search.toLowerCase()) ||
                email.toLowerCase().includes(search.toLowerCase());
        });

    return (
        <div className="flex flex-col gap-6">
            {/* Header */}
            <div className="sticky top-[-16px] lg:top-[-16px] z-30 bg-background-light/80 dark:bg-background-dark/80 backdrop-blur-md pt-4 pb-6 mb-2 flex flex-col md:flex-row md:items-end justify-between gap-4 border-b border-slate-200/50 dark:border-slate-800/50">
                <div className="flex flex-col gap-1">
                    <h1 className="text-3xl font-black leading-tight tracking-tight bg-gradient-to-r from-primary to-blue-500 bg-clip-text text-transparent drop-shadow-sm">Fournisseurs</h1>
                    <p className="text-slate-500 dark:text-slate-400 text-sm font-medium">Gérez vos partenariats et le catalogue d'approvisionnement.</p>
                </div>
                <div className="flex items-center gap-3">
                    <button
                        onClick={handleExport}
                        className="flex items-center gap-2 px-4 py-2 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 font-semibold rounded-lg transition-all text-sm"
                    >
                        <span className="material-symbols-outlined text-xl">ios_share</span>
                        Exporter
                    </button>
                    <button onClick={openNew} className="flex items-center gap-2 px-4 py-2 bg-primary hover:bg-primary/90 text-white font-semibold rounded-lg transition-all shadow-lg shadow-primary/20 text-sm">
                        <span className="material-symbols-outlined text-xl">add</span>
                        Nouveau fournisseur
                    </button>
                </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-2">
                <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl">
                    <div className="flex items-center justify-between mb-4">
                        <div className="size-12 rounded-xl bg-blue-500/10 text-blue-500 flex items-center justify-center">
                            <span className="material-symbols-outlined">groups</span>
                        </div>
                        {stats && <span className="text-xs font-bold text-green-500 bg-green-500/10 px-2 py-1 rounded-full">Total</span>}
                    </div>
                    <p className="text-slate-500 dark:text-slate-400 text-sm font-medium">Total Fournisseurs</p>
                    <h3 className="text-3xl font-bold mt-1">{stats?.total || 0}</h3>
                </div>
                <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl">
                    <div className="flex items-center justify-between mb-4">
                        <div className="size-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
                            <span className="material-symbols-outlined">category</span>
                        </div>
                    </div>
                    <p className="text-slate-500 dark:text-slate-400 text-sm font-medium">Catégories de produits</p>
                    <h3 className="text-3xl font-bold mt-1">{stats?.categories || 0}</h3>
                </div>
                <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl">
                    <div className="flex items-center justify-between mb-4">
                        <div className="size-12 rounded-xl bg-amber-500/10 text-amber-500 flex items-center justify-center">
                            <span className="material-symbols-outlined">pending_actions</span>
                        </div>
                    </div>
                    <p className="text-slate-500 dark:text-slate-400 text-sm font-medium">Fournisseurs inactifs</p>
                    <h3 className="text-3xl font-bold mt-1">{stats?.inactifs || 0}</h3>
                </div>
            </div>

            {/* Filters & Table */}
            <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl overflow-hidden shadow-xl shadow-slate-200/50 dark:shadow-none">
                <div className="p-4 border-b border-slate-200 dark:border-slate-800 flex flex-wrap gap-4 items-center">
                    <div className="flex bg-slate-100 dark:bg-slate-800 p-1 rounded-lg">
                        {['Tous', 'Actif', 'Inactif'].map(f => (
                            <button key={f} onClick={() => setFilter(f)}
                                className={`px-4 py-1.5 text-xs font-bold rounded-md transition-all ${filter === f ? 'bg-white dark:bg-slate-700 shadow-sm text-slate-900 dark:text-white' : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'}`}
                            >{f === 'Tous' ? 'Tous' : f + 's'}</button>
                        ))}
                    </div>
                    <div className="ml-auto relative">
                        <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-lg">search</span>
                        <input placeholder="Rechercher..." className="pl-9 pr-4 py-2 bg-slate-100 dark:bg-slate-800 border-none rounded-lg text-sm outline-none focus:ring-2 focus:ring-primary w-56 px-4" value={search} onChange={e => setSearch(e.target.value)} />
                    </div>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse table-fixed min-w-[800px]">
                        <thead>
                            <tr className="bg-slate-50 dark:bg-slate-800/50">
                                <SortableHeader label="Fournisseur" field="name" currentSort={sortBy} currentOrder={sortOrder} onSort={(f, o) => { setSortBy(f); setSortOrder(o); }} className="w-[30%]" />
                                <SortableHeader label="Contact" field="contactEmail" currentSort={sortBy} currentOrder={sortOrder} onSort={(f, o) => { setSortBy(f); setSortOrder(o); }} className="w-[25%]" />
                                <SortableHeader label="Catégorie" field="category" currentSort={sortBy} currentOrder={sortOrder} onSort={(f, o) => { setSortBy(f); setSortOrder(o); }} className="w-[15%]" />
                                <SortableHeader label="Statut" field="status" currentSort={sortBy} currentOrder={sortOrder} onSort={(f, o) => { setSortBy(f); setSortOrder(o); }} className="w-[15%]" />
                                <PermissionGuard permission="sup.manage">
                                    <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider text-right w-[15%]">Actions</th>
                                </PermissionGuard>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                            {loading ? (
                                <tr>
                                    <td colSpan="5" className="px-6 py-20 text-center">
                                        <div className="animate-spin size-8 border-4 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
                                        <p className="text-slate-500 font-bold">Chargement des fournisseurs...</p>
                                    </td>
                                </tr>
                            ) : filtered.length === 0 ? (
                                <tr>
                                    <td colSpan="5" className="px-6 py-12 text-center text-slate-400">
                                        <span className="material-symbols-outlined text-4xl mb-2 block">storefront</span>
                                        Aucun fournisseur trouvé.
                                    </td>
                                </tr>
                            ) : (
                                filtered.map(s => (
                                    <tr key={s.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/30 transition-colors cursor-pointer" onClick={() => navigate(`/suppliers/${s.id}`)}>
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-3">
                                                <div className="size-10 rounded-lg bg-slate-200 dark:bg-slate-800 flex items-center justify-center font-bold text-slate-500 text-sm">{getInitials(s.name)}</div>
                                                <div>
                                                    <p className="font-semibold text-sm">{s.name}</p>
                                                    <p className="text-xs text-slate-500">ID: FR-{String(s.id).padStart(4, '0')}</p>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="text-sm">
                                                <p className="text-slate-900 dark:text-slate-100">{s.contactEmail || '—'}</p>
                                                <p className="text-slate-500">{s.contactPhone || '—'}</p>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            {s.category ? (
                                                <span className="px-2 py-1 rounded-md bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 text-xs font-medium border border-blue-200 dark:border-blue-800">{s.category}</span>
                                            ) : <span className="text-slate-400 text-xs">—</span>}
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className={`flex items-center gap-1.5 text-xs font-bold ${s.status === 'Actif' ? 'text-emerald-500' : 'text-slate-400'}`}>
                                                <span className={`size-1.5 rounded-full ${s.status === 'Actif' ? 'bg-emerald-500' : 'bg-slate-400'}`}></span>
                                                {s.status}
                                            </span>
                                        </td>
                                        <PermissionGuard permission="sup.manage">
                                            <td className="px-6 py-4 text-right" onClick={e => e.stopPropagation()}>
                                                <div className="flex justify-end gap-1">
                                                    <button onClick={() => handleDelete(s.id)} className="p-2 text-slate-400 hover:text-red-500 transition-colors rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800">
                                                        <span className="material-symbols-outlined text-lg">delete</span>
                                                    </button>
                                                </div>
                                            </td>
                                        </PermissionGuard>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
                <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={setCurrentPage}
                    limit={limit}
                    onLimitChange={(l) => { setLimit(l); setCurrentPage(1); }}
                    totalItems={totalSuppliers}
                />
            </div>

            {/* Modal Premium */}
            {showModal && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-md animate-in fade-in duration-300">
                    <div className="bg-white dark:bg-slate-900 rounded-[2.5rem] shadow-2xl shadow-primary/20 w-full max-w-2xl flex flex-col max-h-[95vh] border border-slate-200 dark:border-slate-800 overflow-hidden animate-in zoom-in-95 duration-500">

                        {/* Premium Header with Gradient */}
                        <div className="shrink-0 px-8 py-6 relative overflow-hidden bg-gradient-to-br from-primary/10 via-primary/5 to-transparent dark:from-primary/20 dark:via-primary/10 dark:to-transparent border-b border-primary/10 dark:border-primary/20">
                            <div className="relative z-10 flex justify-between items-center">
                                <div className="flex gap-4 items-center">
                                    <div className="size-12 rounded-2xl bg-primary/10 flex items-center justify-center shadow-inner">
                                        <span className="material-symbols-outlined text-primary text-[28px]">
                                            {editingSupplier ? 'edit' : 'person_add'}
                                        </span>
                                    </div>
                                    <div>
                                        <h2 className="text-xl font-black text-slate-900 dark:text-white leading-tight">
                                            {editingSupplier ? 'Modifier le Fournisseur' : 'Nouveau Fournisseur'}
                                        </h2>
                                        <p className="text-[10px] text-slate-500 font-bold uppercase tracking-[0.2em] opacity-70">
                                            {editingSupplier ? `Réf: FR-${String(editingSupplier.id).padStart(4, '0')}` : 'Création d\'un nouveau partenaire'}
                                        </p>
                                    </div>
                                </div>
                                <button
                                    onClick={() => setShowModal(false)}
                                    className="size-10 flex items-center justify-center rounded-xl text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-white dark:hover:bg-slate-800 shadow-sm hover:shadow transition-all"
                                >
                                    <span className="material-symbols-outlined">close</span>
                                </button>
                            </div>
                        </div>
                        <form onSubmit={handleSubmit} className="p-10 space-y-6 overflow-y-auto flex-1 custom-scrollbar">
                            <div className="grid grid-cols-2 gap-6">
                                <div className="col-span-2 space-y-1.5">
                                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] ml-1">Nom du Fournisseur *</label>
                                    <div className="relative group">
                                        <div className="absolute left-3 top-1/2 -translate-y-1/2 size-8 rounded-lg bg-primary/10 flex items-center justify-center group-focus-within:bg-primary group-focus-within:text-white transition-all">
                                            <span className="material-symbols-outlined text-[18px]">store</span>
                                        </div>
                                        <input
                                            required
                                            className="w-full h-11 pl-14 pr-4 bg-slate-50 dark:bg-slate-800/50 border-2 border-slate-100 dark:border-slate-800 rounded-xl outline-none focus:border-primary font-bold text-sm transition-all"
                                            value={formData.name}
                                            onChange={e => setFormData({ ...formData, name: e.target.value })}
                                            placeholder="Nom commercial..."
                                        />
                                    </div>
                                </div>

                                <div className="space-y-1.5">
                                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] ml-1">E-mail de Contact</label>
                                    <div className="relative group">
                                        <div className="absolute left-3 top-1/2 -translate-y-1/2 size-8 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center group-focus-within:bg-primary group-focus-within:text-white transition-all">
                                            <span className="material-symbols-outlined text-[18px]">mail</span>
                                        </div>
                                        <input
                                            type="email"
                                            className="w-full h-11 pl-14 pr-4 bg-slate-50 dark:bg-slate-800/50 border-2 border-slate-100 dark:border-slate-800 rounded-xl outline-none focus:border-primary font-bold text-sm transition-all"
                                            value={formData.contactEmail}
                                            onChange={e => setFormData({ ...formData, contactEmail: e.target.value })}
                                            placeholder="contact@fournisseur.com"
                                        />
                                    </div>
                                </div>

                                <div className="space-y-1.5">
                                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] ml-1">Téléphone</label>
                                    <div className="relative group">
                                        <div className="absolute left-3 top-1/2 -translate-y-1/2 size-8 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center group-focus-within:bg-primary group-focus-within:text-white transition-all">
                                            <span className="material-symbols-outlined text-[18px]">call</span>
                                        </div>
                                        <input
                                            className="w-full h-11 pl-14 pr-4 bg-slate-50 dark:bg-slate-800/50 border-2 border-slate-100 dark:border-slate-800 rounded-xl outline-none focus:border-primary font-bold text-sm transition-all"
                                            value={formData.contactPhone}
                                            onChange={e => setFormData({ ...formData, contactPhone: e.target.value })}
                                            placeholder="+33..."
                                        />
                                    </div>
                                </div>

                                <div className="space-y-1.5">
                                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] ml-1">Catégorie d'activité</label>
                                    <div className="relative group">
                                        <div className="absolute left-3 top-1/2 -translate-y-1/2 size-8 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center group-focus-within:bg-primary group-focus-within:text-white transition-all">
                                            <span className="material-symbols-outlined text-[18px]">category</span>
                                        </div>
                                        <select
                                            required
                                            className="w-full h-11 pl-14 pr-10 bg-slate-50 dark:bg-slate-800/50 border-2 border-slate-100 dark:border-slate-800 rounded-xl outline-none focus:border-primary font-bold text-sm transition-all appearance-none"
                                            value={formData.category}
                                            onChange={e => setFormData({ ...formData, category: e.target.value })}
                                        >
                                            <option value="">-- Choisir --</option>
                                            {categories.map(cat => (
                                                <option key={cat} value={cat}>{cat}</option>
                                            ))}
                                        </select>
                                        <span className="material-symbols-outlined absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none">expand_more</span>
                                    </div>
                                </div>

                                <div className="space-y-1.5">
                                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] ml-1">Statut Partenariat</label>
                                    <div className="relative group">
                                        <div className="absolute left-3 top-1/2 -translate-y-1/2 size-8 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center group-focus-within:bg-primary group-focus-within:text-white transition-all">
                                            <span className="material-symbols-outlined text-[18px]">verified_user</span>
                                        </div>
                                        <select
                                            required
                                            className="w-full h-11 pl-14 pr-10 bg-slate-50 dark:bg-slate-800/50 border-2 border-slate-100 dark:border-slate-800 rounded-xl outline-none focus:border-primary font-bold text-sm transition-all appearance-none"
                                            value={formData.status}
                                            onChange={e => setFormData({ ...formData, status: e.target.value })}
                                        >
                                            <option value="Actif">Actif</option>
                                            <option value="Inactif">Inactif</option>
                                        </select>
                                        <span className="material-symbols-outlined absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none">expand_more</span>
                                    </div>
                                </div>

                                <div className="col-span-2 space-y-1.5 relative">
                                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] ml-1">Adresse Postale</label>
                                    <div className="relative group">
                                        <div className="absolute left-3 top-1/2 -translate-y-1/2 size-8 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center group-focus-within:bg-primary group-focus-within:text-white transition-all">
                                            <span className="material-symbols-outlined text-[18px]">location_on</span>
                                        </div>
                                        <input
                                            className="w-full h-11 pl-14 pr-4 bg-slate-50 dark:bg-slate-800/50 border-2 border-slate-100 dark:border-slate-800 rounded-xl outline-none focus:border-primary font-bold text-sm transition-all"
                                            value={formData.address}
                                            onChange={e => handleAddressSearch(e.target.value)}
                                            placeholder="Chercher une adresse..."
                                        />
                                    </div>
                                    {showAddressSuggestions && addressSuggestions.length > 0 && (
                                        <ul className="absolute z-[1100] bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 w-full mt-1 rounded-2xl shadow-2xl max-h-48 overflow-y-auto custom-scrollbar p-2 space-y-1 border-t-0 -mt-2 pt-4">
                                            {addressSuggestions.map((s, i) => (
                                                <li key={i} className="px-4 py-2.5 hover:bg-primary/5 hover:text-primary dark:hover:bg-primary/10 cursor-pointer text-xs font-bold rounded-xl transition-all border border-transparent hover:border-primary/10" onClick={() => selectAddress(s)}>
                                                    {s.display_name}
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>

                                <div className="space-y-1.5">
                                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] ml-1">Site Web</label>
                                    <div className="relative group">
                                        <div className="absolute left-3 top-1/2 -translate-y-1/2 size-8 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center group-focus-within:bg-primary group-focus-within:text-white transition-all">
                                            <span className="material-symbols-outlined text-[18px]">language</span>
                                        </div>
                                        <input
                                            className="w-full h-11 pl-14 pr-4 bg-slate-50 dark:bg-slate-800/50 border-2 border-slate-100 dark:border-slate-800 rounded-xl outline-none focus:border-primary font-bold text-sm transition-all"
                                            value={formData.website}
                                            onChange={e => setFormData({ ...formData, website: e.target.value })}
                                            placeholder="https://..."
                                        />
                                    </div>
                                </div>

                                <div className="space-y-1.5">
                                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] ml-1">Certifications</label>
                                    <div className="relative group">
                                        <div className="absolute left-3 top-1/2 -translate-y-1/2 size-8 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center group-focus-within:bg-primary group-focus-within:text-white transition-all">
                                            <span className="material-symbols-outlined text-[18px]">verified</span>
                                        </div>
                                        <input
                                            placeholder="Ex: ISO 9001, BIO..."
                                            className="w-full h-11 pl-14 pr-4 bg-slate-50 dark:bg-slate-800/50 border-2 border-slate-100 dark:border-slate-800 rounded-xl outline-none focus:border-primary font-bold text-sm transition-all"
                                            value={formData.certifications}
                                            onChange={e => setFormData({ ...formData, certifications: e.target.value })}
                                        />
                                    </div>
                                </div>

                                <div className="space-y-1.5">
                                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] ml-1">Délai de Livraison (Jours)</label>
                                    <div className="relative group">
                                        <div className="absolute left-3 top-1/2 -translate-y-1/2 size-8 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center group-focus-within:bg-primary group-focus-within:text-white transition-all">
                                            <span className="material-symbols-outlined text-[18px]">schedule</span>
                                        </div>
                                        <input
                                            placeholder="Ex: 5"
                                            className="w-full h-11 pl-14 pr-4 bg-slate-50 dark:bg-slate-800/50 border-2 border-slate-100 dark:border-slate-800 rounded-xl outline-none focus:border-primary font-bold text-sm transition-all"
                                            value={formData.avgDeliveryTime}
                                            onChange={e => setFormData({ ...formData, avgDeliveryTime: e.target.value })}
                                        />
                                    </div>
                                </div>

                                <div className="space-y-1.5">
                                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] ml-1">Franco de port (€)</label>
                                    <div className="relative group">
                                        <div className="absolute left-3 top-1/2 -translate-y-1/2 size-8 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center group-focus-within:bg-primary group-focus-within:text-white transition-all">
                                            <span className="material-symbols-outlined text-[18px]">local_shipping</span>
                                        </div>
                                        <input
                                            type="number"
                                            step="0.01"
                                            min="0"
                                            placeholder="Ex: 500"
                                            className="w-full h-11 pl-14 pr-4 bg-slate-50 dark:bg-slate-800/50 border-2 border-slate-100 dark:border-slate-800 rounded-xl outline-none focus:border-primary font-black text-sm transition-all font-mono"
                                            value={formData.francoPort}
                                            onChange={e => setFormData({ ...formData, francoPort: e.target.value })}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="flex gap-4 pt-8 bg-white dark:bg-slate-900 border-t border-slate-100 dark:border-slate-800">
                                <button
                                    type="button"
                                    onClick={() => setShowModal(false)}
                                    className="px-8 h-12 rounded-xl text-slate-500 font-black hover:bg-slate-200 dark:hover:bg-slate-800 transition-all uppercase text-[10px] tracking-[0.2em]"
                                >
                                    Annuler
                                </button>
                                <button
                                    type="submit"
                                    className="flex-1 h-12 rounded-xl bg-primary text-white font-black hover:bg-primary/90 transition-all shadow-xl shadow-primary/20 hover:scale-[1.02] active:scale-[0.98] uppercase text-[10px] tracking-[0.2em] flex items-center justify-center gap-3"
                                >
                                    <span className="material-symbols-outlined text-[20px]">save</span>
                                    {editingSupplier ? 'Mettre à jour' : 'Créer le fournisseur'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SupplierManagement;
